// UserProfile — public-facing user profile component used by /u/:slug.
//
// Sections:
//   Hero            — avatar, display name, bio, Follow button, location
//   ActivityChart   — last 365 days of activity, GitHub-style heatmap
//   Tabs            — Projects | Integrations | Agents | Followers | Following
//   <Tab content>
//
// Reads the viewed user from `s.root._profileViewed` (populated by
// pages/userProfile.js#onCreate). The Follow button writes to
// `s.root.followingEmails` via the same helper the marketplace uses.
//
// This component is wired into the project's components/index.js so
// pages/userProfile.js can mount it. Activity chart reuses the shared
// `ActivityChart` (designed to be data-source agnostic).

import { findPerson, patchPeopleList } from '../functions/peopleUtils.js'
import { getSDK } from '../functions/sdk.js'
import { projectScreenshotUrl } from '../functions/projects.js'

export const UserProfile = {
  // FA204 / frank trap — every helper below lives in `scope`, not module
  // scope: a bare module-scope const/function silently reads as undefined
  // once this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Called via
  // `el.scope.<name>(...)` from any descendant factory/handler (`scope:`
  // merges onto every instance in this tree). Regular method syntax (not
  // arrows) — `el.scope.foo()` binds `this` to `el.scope`, so sibling
  // helpers call each other via `this.<name>(...)`. `readFollows`/
  // `writeFollows`/`isFollowing`/`toggleFollow`/`isSelf` are DUPLICATED into
  // UserProfileFollowRow's own scope below — `el.scope` only resolves
  // within the tree of the specific top-level export it's defined on, not
  // across sibling top-level exports in this file.
  scope: {
    // Debounced field saver — keyed per field so name + bio + location
    // don't cancel each other's writes. Mirrors the pattern in
    // MemberProfile._patchUser.
    saveTimers: {},

    debouncedSave(root, userId, patch) {
      if (!root || !userId) return
      // Optimistic local update so the UI tracks the user's typing.
      const merged = { ...(root._profileViewed || {}), ...patch }
      // Sync both directories so values persist after navigate. Closes #2220.
      root.update({
        _profileViewed: merged,
        _profileSaving: true,
        // Session flag — the FIRST edit this page-view has actually made.
        // SaveStatus.hide below gates on this alongside _profileEditing/
        // isSelf so the "Saved" fallback text can't render the instant edit
        // mode opens, before any keystroke has triggered a save.
        _profileHasSaved: true,
        people: patchPeopleList(root.people, userId, patch),
        peopleAll: patchPeopleList(root.peopleAll, userId, patch)
      })
      const field = Object.keys(patch)[0]
      clearTimeout(this.saveTimers[field])
      this.saveTimers[field] = setTimeout(async () => {
        try {
          // Must be the id-targeted route, NOT `workspaceProject.userProfiles`.
          // That entity is CALLER-scoped: it used to ride the `/sb` proxy, whose
          // `_scopeUrl` did `params.set('user_id', 'eq.' + caller)` — `set`, so the
          // `id` we passed was accepted and then silently overwritten — and it now
          // resolves to the self-scoped `PATCH /core/user-profile`, which ignores
          // `id` by design. Either way, editing ANOTHER user's profile here wrote
          // to the acting user's own row and returned 200. `_profileViewed.userId`
          // is frequently someone else, so this surface was affected.
          // `updateUserHrProfile` throws rather than defaulting to the caller.
          await getSDK().updateUserHrProfile(userId, patch)
          root.update({ _profileSaving: false, _profileSaveError: null })
        } catch (err) {
          console.warn('[userProfile] save failed', err?.message || err)
          // A KEY, not copy — `debouncedSave` is a scope method with no
          // element, so the SaveStatus render site resolves it. A server
          // message passes through polyglot unchanged.
          root.update({
            _profileSaving: false,
            _profileSaveError: err?.message || 'account.userProfile.saveFailed'
          })
        }
      }, 500)
    },

    toggleEditMode(root) {
      if (!root) return
      root.update({ _profileEditing: !root._profileEditing })
    },

    // Hydrate the user's followers/following list from localStorage. The
    // store mirrors what marketplace persists so the same source of
    // truth backs the workspace and marketplace surfaces. Loaded once
    // per profile mount.
    readFollows() {
      if (typeof localStorage === 'undefined') return []
      try {
        const raw = localStorage.getItem('marketplace.follows')
        return Array.isArray(JSON.parse(raw || '[]')) ? JSON.parse(raw) : []
      } catch {
        return []
      }
    },

    writeFollows(list) {
      if (typeof localStorage === 'undefined') return
      try {
        localStorage.setItem('marketplace.follows', JSON.stringify(list))
      } catch {}
    },

    isFollowing(root, email) {
      if (!root || !email) return false
      return (root.followingEmails || [] || this.readFollows())
        .map((e) => (e || '').toLowerCase())
        .includes(email.toLowerCase())
    },

    toggleFollow(root, email) {
      if (!root || !email) return
      const cur = (root.followingEmails || this.readFollows()).map((e) => (e || '').toLowerCase())
      const lower = email.toLowerCase()
      const next = cur.includes(lower) ? cur.filter((e) => e !== lower) : [...cur, lower]
      root.update({ followingEmails: next })
      this.writeFollows(next)
    },

    // Self-check — true when the profile being viewed belongs to the
    // signed-in user. Robust against an unpopulated `currentUserEmail`:
    //   1. honors the `_isSelf` marker the slug resolver stamps on the
    //      viewed record when getMe() matched the slug (pages/userProfile.js)
    //   2. matches by user id (currentUserId <-> viewed userId)
    //   3. matches by email (case-insensitive)
    // Any one signal hiding the Follow control is correct — better to hide
    // it on a near-miss than to let a user follow themselves (self-follow).
    isSelf(root, email) {
      if (!root) return false
      const viewed = root._profileViewed || {}
      if (viewed._isSelf) return true
      const meId = (root.currentUserId || '').toString()
      const viewedId = (viewed.userId || viewed.id || viewed._id || '').toString()
      if (meId && viewedId && meId === viewedId) return true
      const meEmail = (root.currentUserEmail || '').toLowerCase()
      const cmp = (email || viewed.email || '').toLowerCase()
      if (!cmp) return false
      return meEmail === cmp
    },

    // Mock followers / following — until the backend lands, we synthesize
    // names from people directory + the current user's follow list. For
    // the viewed user's perspective: their `following` is everyone whose
    // row appears in the local follow store; their `followers` is the
    // inverse view computed from a global follow store (capped at 8 to
    // keep the UI lightweight).
    profileFollows(root, viewedEmail) {
      const me = (root?.currentUserEmail || '').toLowerCase()
      if (!viewedEmail) return { followers: [], following: [] }
      const all = (root.peopleAll || []).concat(root.people || [])
      if (viewedEmail.toLowerCase() === me) {
        const followingEmails = (root.followingEmails || this.readFollows()).map((e) =>
          (e || '').toLowerCase()
        )
        const following = followingEmails.map(
          (e) => findPerson(root, e) || { email: e, name: e.split('@')[0] }
        )
        // Followers — synthesize from team people. Without backend support,
        // we surface up to 4 active members as "followers" so the UI has
        // shape; a real backend will replace this read.
        const followers = all.slice(0, 4).filter((p) => (p.email || '').toLowerCase() !== me)
        return { followers, following }
      }
      // Viewing someone else: followers = anyone who follows them (best
      // effort, including the current user if applicable); following =
      // empty until the backend lands.
      const followers = []
      const myFollows = (root.followingEmails || this.readFollows()).map((e) =>
        (e || '').toLowerCase()
      )
      if (myFollows.includes(viewedEmail.toLowerCase())) {
        // A KEY, not copy — no element here either. The two follower render
        // sites (UserProfilePersonRow's Name / Avatar initial) resolve it, and
        // a real person's name passes through polyglot unchanged.
        const me_p = findPerson(root, root.currentUserEmail) || {
          email: root.currentUserEmail,
          name: 'account.userProfile.you'
        }
        followers.push(me_p)
      }
      return { followers, following: [] }
    },

    // Persisted "agents" are conceptual integrations the user has
    // installed. Until the dedicated agents endpoint lands we surface
    // nothing; the tab still renders an empty state so the UX is
    // future-ready.
    profileAgents(root, viewedEmail) {
      if (!viewedEmail) return []
      // For the current user: surface entries from `_integrationHub`
      // (populated by marketplace's hydrate). Other users return [].
      if ((root.currentUserEmail || '').toLowerCase() !== viewedEmail.toLowerCase()) return []
      const hub = root._integrationHub
      const items = (hub?.integrations || []).filter((i) => i.kind === 'mcp')
      return items.map((i) => ({
        id: i.id || i.kind,
        name: i.name || i.kind,
        description: i.description || ''
      }))
    },

    profileIntegrations(root, viewedEmail) {
      if (!viewedEmail) return []
      if ((root.currentUserEmail || '').toLowerCase() !== viewedEmail.toLowerCase()) return []
      const hub = root._integrationHub
      const items = (hub?.integrations || []).filter((i) => i.kind !== 'mcp')
      return items.map((i) => ({
        id: i.id || i.kind,
        name: i.name || i.kind,
        description: i.description || ''
      }))
    },

    profileProjects(root) {
      const list = root._profileProjects
      if (!Array.isArray(list)) return []
      return list
    }
  },

  flow: 'y',
  gap: 'C',
  padding: 'C B1',
  maxWidth: 'L+L',
  width: '100%',
  margin: '0 auto',

  Hero: {
    flow: 'x',
    gap: 'B',
    align: 'center',
    padding: 'B 0',
    // Was `borderBottom: 'hairline solid line'` — divider-as-chrome, banned
    // by design-direction.md §8 / DESIGN_LANGUAGE §2.5. UserProfile's own
    // root `gap: 'C'` between Hero/ActivitySection/Tabs/TabContent already
    // gives every section boundary a full large-token whitespace gap on top
    // of this padding, so the line was pure decoration, not the separation
    // mechanism.

    Avatar: {
      align: 'center center',
      boxSize: 'F',
      minWidth: 'F',
      round: '50%',
      src: (el, s) => el.call('avatarUrlFor', s.root?._profileViewed?.email)
    },

    Info: {
      flow: 'y',
      gap: 'V',
      flex: '1',

      Name: {
        hide: (el, s) =>
          s.root?._profileEditing && el.scope.isSelf(s.root, s.root?._profileViewed?.email),
        tag: 'h1',
        margin: '0',
        fontSize: 'D',
        fontWeight: '700',
        letterSpacing: '-0.02em',
        color: 'title',
        text: (el, s) =>
          s.root?._profileViewed?.name ||
          s.root?._profileSlug ||
          el.call('polyglot', 'account.userProfile.fallbackName')
      },

      NameField: {
        hide: (el, s) =>
          !(s.root?._profileEditing && el.scope.isSelf(s.root, s.root?._profileViewed?.email)),
        tag: 'input',
        type: 'text',
        // Display-size text (D, above 16px at every root): the shell's touch
        // focus-zoom floor (workspace app.js) would cut it to its parent's
        // size, and a field this large never zooms anyway.
        dataDisplayText: true,
        padding: 'X Z',
        borderRadius: 'Z',
        border: 'hairline solid line',
        background: 'document',
        color: 'title',
        fontSize: 'D',
        fontWeight: '700',
        letterSpacing: '-0.02em',
        width: '100%',
        maxWidth: 'I',
        placeholder: (el) => el.call('polyglot', 'account.userProfile.placeholder.name'),
        value: (el, s) => s.root?._profileViewed?.name || '',
        onInput: (e, el, s) => {
          const userId = s.root?._profileViewed?.userId
          el.scope.debouncedSave(s.root, userId, { name: e.target.value })
        }
      },

      Handle: {
        tag: 'span',
        fontSize: 'A',
        color: 'caption',
        text: (el, s) => {
          const v = s.root?._profileViewed || {}
          const slug = v.slug || (v.email || '').split('@')[0] || s.root?._profileSlug
          return slug ? `@${slug}` : ''
        }
      },

      Bio: {
        hide: (el, s) => {
          if (s.root?._profileEditing && el.scope.isSelf(s.root, s.root?._profileViewed?.email))
            return true
          return !(s.root?._profileViewed?.bio || s.root?._profileViewed?.position)
        },
        tag: 'p',
        margin: '0',
        marginTop: 'V',
        fontSize: 'A',
        color: 'subtitle',
        lineHeight: '1.5',
        maxWidth: 'K_default',
        text: (el, s) => {
          const v = s.root?._profileViewed || {}
          return v.bio || v.position || ''
        }
      },

      // Edit-mode bio — surfaces only when the viewer is the owner and
      // _profileEditing is on. Writes back through `el.scope.debouncedSave`
      // (sdk.updateUserHrProfile — id-targeted). The legacy `position`
      // column doubles as the bio for accounts that don't have a
      // dedicated `bio` field — we write `bio` first, then `position`
      // as a fallback for back-compat.
      BioField: {
        hide: (el, s) =>
          !(s.root?._profileEditing && el.scope.isSelf(s.root, s.root?._profileViewed?.email)),
        tag: 'textarea',
        marginTop: 'V',
        width: '100%',
        maxWidth: 'K_default',
        minHeight: 'D',
        padding: 'Z A',
        borderRadius: 'Z',
        border: 'hairline solid line',
        background: 'document',
        color: 'title',
        fontSize: 'A',
        fontFamily: 'inherit',
        lineHeight: '1.5',
        resize: 'vertical',
        placeholder: (el) => el.call('polyglot', 'account.userProfile.placeholder.bio'),
        value: (el, s) => {
          const v = s.root?._profileViewed || {}
          return v.bio ?? v.position ?? ''
        },
        onInput: (e, el, s) => {
          const userId = s.root?._profileViewed?.userId
          el.scope.debouncedSave(s.root, userId, { bio: e.target.value, position: e.target.value })
        }
      },

      Meta: {
        flow: 'x',
        gap: 'A',
        marginTop: 'V',
        fontSize: 'Z',
        color: 'caption',
        flexWrap: 'wrap',

        Location: {
          tag: 'span',
          hide: (el, s) => {
            if (s.root?._profileEditing && el.scope.isSelf(s.root, s.root?._profileViewed?.email))
              return true
            return !(s.root?._profileViewed?.location || s.root?._profileViewed?.city)
          },
          text: (el, s) => {
            const v = s.root?._profileViewed || {}
            return `📍 ${v.location || v.city || ''}`
          }
        },

        LocationField: {
          hide: (el, s) =>
            !(s.root?._profileEditing && el.scope.isSelf(s.root, s.root?._profileViewed?.email)),
          tag: 'input',
          type: 'text',
          padding: 'X Z',
          borderRadius: 'Y',
          border: 'hairline solid line',
          background: 'document',
          color: 'title',
          fontSize: 'Z',
          minWidth: 'F',
          placeholder: (el) => el.call('polyglot', 'account.userProfile.placeholder.location'),
          value: (el, s) => {
            const v = s.root?._profileViewed || {}
            return v.location || v.city || ''
          },
          onInput: (e, el, s) => {
            const userId = s.root?._profileViewed?.userId
            el.scope.debouncedSave(s.root, userId, { location: e.target.value })
          }
        },

        SaveStatus: {
          // Was missing a "has a save actually happened this session" gate:
          // the moment edit mode opens, _profileSaving/_profileSaveError are
          // both falsy, so the text factory below fell through to 'Saved'
          // before any keystroke. _profileHasSaved (set by debouncedSave
          // above) closes that gap.
          hide: (el, s) =>
            !s.root?._profileEditing ||
            !s.root?._profileHasSaved ||
            !el.scope.isSelf(s.root, s.root?._profileViewed?.email),
          tag: 'span',
          fontSize: 'Y',
          fontStyle: 'italic',
          color: (el, s) => (s.root?._profileSaveError ? 'red' : 'caption'),
          text: (el, s) => {
            if (s.root?._profileSaveError)
              return `⚠ ${el.call('polyglot', s.root._profileSaveError)}`
            if (s.root?._profileSaving) return el.call('polyglot', 'account.userProfile.saving')
            return el.call('polyglot', 'account.userProfile.saved')
          }
        },

        Joined: {
          tag: 'span',
          hide: (el, s) => !s.root?._profileViewed?.startDate,
          text: (el, s) => {
            const d = s.root?._profileViewed?.startDate
            if (!d) return ''
            try {
              const dt = new Date(d)
              return el
                .call('polyglot', 'account.userProfile.joined')
                .replace(
                  '{date}',
                  el.call('localizedDate', dt, { month: 'short', year: 'numeric' })
                )
            } catch {
              return ''
            }
          }
        }
      }
    },

    Actions: {
      flow: 'x',
      gap: 'Z',
      align: 'center',

      FollowBtn: {
        extends: 'Button',
        // Self-follow guard (#self-follow). Two compounding causes made the
        // Follow control stay visible on your own profile (both verified live):
        //   1. `Button` sets a STATIC inline `display: inline-flex`; the reactive
        //      `hide` writes `display: none` via an atomic class, which loses to
        //      the inline style — so the button never hid. `display: null` clears
        //      that inline value so the reactive `hide` can win (the InspectBtn /
        //      CanvasPromptTextarea pattern).
        //   2. The viewer identity (currentUserId / currentUserEmail) and the
        //      resolved profile (_profileViewed) land AFTER first paint, so the
        //      `hide` factory must read all of them up front (via `isSelf`)
        //      before any early return, so its effect tracks every signal from
        //      the first pass and re-evaluates once identity hydrates.
        display: null,
        hide: (el, s) => {
          const self = el.scope.isSelf(s.root, s.root?._profileViewed?.email)
          if (!s.root?._profileViewed?.email) return true
          return self
        },
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        fontSize: 'Z2',
        fontWeight: '700',
        cursor: 'pointer',
        // Was an outlined button (`border` + conditional `borderColor`) —
        // banned outright by design-direction.md §8's ban list. §6.2's
        // button law is solid ink OR ghost fill, never outline: the
        // "Following" (secondary) state moves from transparent+border to
        // the `overlay` ghost fill; "Follow" (primary) was already a solid
        // blue fill, so its border was invisible border-on-same-colour
        // already — dropping it is a no-op there.
        background: (el, s) =>
          el.scope.isFollowing(s.root, s.root?._profileViewed?.email) ? 'overlay' : 'blue',
        color: (el, s) =>
          el.scope.isFollowing(s.root, s.root?._profileViewed?.email) ? 'caption' : 'white',
        text: (el, s) =>
          el.call(
            'polyglot',
            el.scope.isFollowing(s.root, s.root?._profileViewed?.email)
              ? 'account.userProfile.following'
              : 'account.userProfile.follow'
          ),
        onClick: (e, el, s) => el.scope.toggleFollow(s.root, s.root?._profileViewed?.email)
      },

      EditBtn: {
        extends: 'Button',
        // Inverse of FollowBtn — shown ONLY on your own profile. Same two fixes:
        // `display: null` so the reactive `hide` beats the static inline
        // `display`, and `isSelf` read up front so the effect tracks identity
        // and re-evaluates on late hydration. Hidden when NOT your own profile,
        // or before the viewed record resolves.
        display: null,
        hide: (el, s) => {
          const self = el.scope.isSelf(s.root, s.root?._profileViewed?.email)
          if (!s.root?._profileViewed?.email) return true
          return !self
        },
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        // Was an outlined button — same fix + rationale as FollowBtn above.
        background: (el, s) => (s.root?._profileEditing ? 'blue' : 'overlay'),
        color: (el, s) => (s.root?._profileEditing ? 'fillInk' : 'caption'),
        fontSize: 'Z2',
        fontWeight: '700',
        cursor: 'pointer',
        ':hover': { color: 'title' },
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?._profileEditing ? 'account.userProfile.editDone' : 'account.userProfile.edit'
          ),
        onClick: (e, el, s) => el.scope.toggleEditMode(s.root)
      }
    }
  },

  ActivitySection: {
    flow: 'y',
    gap: 'Z',
    padding: 'B 0',
    // Divider-as-chrome — same fix + rationale as Hero above.

    heading: 'account.userProfile.activity.title',
    accent: 'green',
    dataKey: '_profileActivity',
    showLegend: true,

    ActivityChart: {}
  },

  Tabs: {
    flow: 'x',
    gap: 'V',
    padding: 'B 0 0',
    // Divider-as-chrome — same fix + rationale as Hero above; the root
    // `gap: 'C'` before TabContent plus TabContent's own top padding 'B'
    // already separate the tab row from its panel.

    childExtends: 'UserProfileTab',
    childrenAs: 'state',
    children: () => [
      { key: 'projects', label: 'account.userProfile.tab.projects' },
      { key: 'integrations', label: 'account.userProfile.tab.integrations' },
      { key: 'agents', label: 'account.userProfile.tab.agents' },
      { key: 'followers', label: 'account.userProfile.tab.followers' },
      { key: 'following', label: 'account.userProfile.tab.following' }
    ]
  },

  TabContent: {
    flow: 'y',
    gap: 'A',
    padding: 'B 0',
    minHeight: 'I',

    ProjectsPane: {
      hide: (el, s) => (s.root?._profileTab || 'projects') !== 'projects',
      flow: 'y',
      gap: 'A',

      Empty: {
        hide: (el, s) => el.scope.profileProjects(s.root).length > 0,
        tag: 'p',
        margin: '0',
        padding: 'A',
        textAlign: 'center',
        color: 'caption',
        fontSize: 'Z',
        text: '{{ account.userProfile.empty.projects | polyglot }}'
      },

      Grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
        gap: 'A',
        childExtends: 'UserProfileProjectCard',
        childrenAs: 'state',
        children: (el, s) => el.scope.profileProjects(s.root)
      }
    },

    IntegrationsPane: {
      hide: (el, s) => (s.root?._profileTab || 'projects') !== 'integrations',
      flow: 'y',
      gap: 'Z',

      Empty: {
        hide: (el, s) =>
          el.scope.profileIntegrations(s.root, s.root?._profileViewed?.email).length > 0,
        tag: 'p',
        margin: '0',
        padding: 'A',
        textAlign: 'center',
        color: 'caption',
        fontSize: 'Z',
        text: '{{ account.userProfile.empty.integrations | polyglot }}'
      },

      List: {
        flow: 'y',
        gap: 'Z',
        childExtends: 'UserProfileIntegrationRow',
        childrenAs: 'state',
        children: (el, s) => el.scope.profileIntegrations(s.root, s.root?._profileViewed?.email)
      }
    },

    AgentsPane: {
      hide: (el, s) => (s.root?._profileTab || 'projects') !== 'agents',
      flow: 'y',
      gap: 'Z',

      Empty: {
        hide: (el, s) => el.scope.profileAgents(s.root, s.root?._profileViewed?.email).length > 0,
        tag: 'p',
        margin: '0',
        padding: 'A',
        textAlign: 'center',
        color: 'caption',
        fontSize: 'Z',
        text: '{{ account.userProfile.empty.agents | polyglot }}'
      },

      List: {
        flow: 'y',
        gap: 'Z',
        childExtends: 'UserProfileIntegrationRow',
        childrenAs: 'state',
        children: (el, s) => el.scope.profileAgents(s.root, s.root?._profileViewed?.email)
      }
    },

    FollowersPane: {
      hide: (el, s) => (s.root?._profileTab || 'projects') !== 'followers',
      flow: 'y',
      gap: 'Z',

      Empty: {
        hide: (el, s) =>
          el.scope.profileFollows(s.root, s.root?._profileViewed?.email).followers.length > 0,
        tag: 'p',
        margin: '0',
        padding: 'A',
        textAlign: 'center',
        color: 'caption',
        fontSize: 'Z',
        text: '{{ account.userProfile.empty.followers | polyglot }}'
      },

      List: {
        flow: 'y',
        gap: 'Z',
        childExtends: 'UserProfileFollowRow',
        childrenAs: 'state',
        children: (el, s) =>
          el.scope.profileFollows(s.root, s.root?._profileViewed?.email).followers
      }
    },

    FollowingPane: {
      hide: (el, s) => (s.root?._profileTab || 'projects') !== 'following',
      flow: 'y',
      gap: 'Z',

      Empty: {
        hide: (el, s) =>
          el.scope.profileFollows(s.root, s.root?._profileViewed?.email).following.length > 0,
        tag: 'p',
        margin: '0',
        padding: 'A',
        textAlign: 'center',
        color: 'caption',
        fontSize: 'Z',
        text: '{{ account.userProfile.empty.following | polyglot }}'
      },

      List: {
        flow: 'y',
        gap: 'Z',
        childExtends: 'UserProfileFollowRow',
        childrenAs: 'state',
        children: (el, s) =>
          el.scope.profileFollows(s.root, s.root?._profileViewed?.email).following
      }
    }
  }
}

export const UserProfileTab = {
  tag: 'button',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 'Z A',
  fontSize: 'Z',
  fontWeight: '700',
  // Was `position: 'relative'` to host the Underline child below — dead
  // once that's gone.
  color: (el, s) => ((s.root?._profileTab || 'projects') === s.key ? 'title' : 'caption'),
  ':hover': { color: 'title' },
  text: (el, s) => el.call('polyglot', s.label || ''),
  onClick: (e, el, s) => s.root?.update({ _profileTab: s.key })

  // Underline (removed): a `background: 'blue'` bar under the active tab —
  // underline-as-chrome (design-direction.md §8) AND hue-marking-selection
  // (DESIGN_LANGUAGE §4.3/§8.1: "selected" is monochrome, hue never marks
  // it) — a double violation of the same kind brand's TabsLink.js was
  // already fixed for. The tab is already fully legible active/inactive
  // from ink alone (the color factory above, title vs caption) — the same
  // "ink alone is enough" finding that fix made explicit.
}

export const UserProfileProjectCard = {
  // FA204 / frank trap — see UserProfile.scope above for the full note.
  // `gradientFor` is only needed here, so it's not duplicated anywhere else.
  scope: {
    gradientFor(id) {
      let h = 0
      for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
      const hue = h % 360
      return `linear-gradient(135deg, hsl(${hue} 60% 38%) 0%, hsl(${(hue + 50) % 360} 55% 22%) 100%)`
    }
  },

  flow: 'y',
  gap: '0',
  borderRadius: 'Z',
  overflow: 'hidden',
  // Was a full `hairline solid line` outline — a card outline, banned by
  // design-direction.md §8's ban list. `background: 'dialog'` (an opaque,
  // one-step-up surface, DESIGN_LANGUAGE §2.2) already separates the card
  // from its grid without a stroke.
  background: 'dialog',
  cursor: 'pointer',
  transition: '180ms defaultBezier box-shadow, 180ms defaultBezier transform',
  // Hover swaps the accent-border cue for the real elevation token instead
  // (§2.3 — `float` is exactly the "floating/lifting card" moment) rather
  // than reintroducing a stroke.
  ':hover': { shadow: 'float', transform: 'translateY(-2px)' },
  onClick: (e, el, s) => {
    const owner = s.workspace?.organization?.slug || s.workspace?.slug || ''
    const key = s.key || ''
    if (owner && key && typeof window !== 'undefined') {
      window.open(`/${owner}/${key}`, '_self')
    }
  },

  Cover: {
    aspectRatio: '16 / 9',
    width: '100%',
    background: (el, s) => {
      const owner = s.workspace?.organization?.slug || s.workspace?.slug || ''
      const key = s.key || ''
      const grad = el.scope.gradientFor(`${owner}/${key}`)
      const url = owner && key ? projectScreenshotUrl({ owner, key }) || '' : ''
      if (url) return `url("${url}") center / cover no-repeat, ${grad}`
      return grad
    },
    __order: ['aspectRatio', 'width', 'background']
  },

  Body: {
    flow: 'y',
    gap: 'V',
    padding: 'Z A',

    Name: {
      tag: 'h4',
      margin: '0',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.name || s.key || ''
    },

    Desc: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.description || ''
    }
  }
}

export const UserProfileIntegrationRow = {
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'Z A',
  borderRadius: 'Z',
  // Was a card-outline `hairline solid line` border — banned (design-
  // direction.md §8). The row already sits in a `gap`-spaced list AND
  // carries its own opaque `background: 'dialog'` fill, so the border was
  // fully redundant with the separation the fill+gap already provide.
  background: 'dialog',

  Icon: {
    align: 'center center',
    width: 'B',
    height: 'B',
    minWidth: 'B',
    borderRadius: 'Y',
    background: 'overlay',
    color: 'title',
    fontWeight: '700',
    fontSize: 'A',
    text: (el, s) => (s.name || '?')[0].toUpperCase()
  },

  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',

    Name: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.name || ''
    },

    Desc: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      text: (el, s) => s.description || ''
    }
  }
}

export const UserProfileFollowRow = {
  // FA204 / frank trap — see UserProfile.scope above for the full note.
  // readFollows/writeFollows/isFollowing/toggleFollow/isSelf are
  // DUPLICATED from UserProfile's own scope — `el.scope` doesn't resolve
  // across sibling top-level exports in this file, only within the tree of
  // the export it's declared on.
  scope: {
    readFollows() {
      if (typeof localStorage === 'undefined') return []
      try {
        const raw = localStorage.getItem('marketplace.follows')
        return Array.isArray(JSON.parse(raw || '[]')) ? JSON.parse(raw) : []
      } catch {
        return []
      }
    },

    writeFollows(list) {
      if (typeof localStorage === 'undefined') return
      try {
        localStorage.setItem('marketplace.follows', JSON.stringify(list))
      } catch {}
    },

    isFollowing(root, email) {
      if (!root || !email) return false
      return (root.followingEmails || [] || this.readFollows())
        .map((e) => (e || '').toLowerCase())
        .includes(email.toLowerCase())
    },

    toggleFollow(root, email) {
      if (!root || !email) return
      const cur = (root.followingEmails || this.readFollows()).map((e) => (e || '').toLowerCase())
      const lower = email.toLowerCase()
      const next = cur.includes(lower) ? cur.filter((e) => e !== lower) : [...cur, lower]
      root.update({ followingEmails: next })
      this.writeFollows(next)
    },

    isSelf(root, email) {
      if (!root) return false
      const viewed = root._profileViewed || {}
      if (viewed._isSelf) return true
      const meId = (root.currentUserId || '').toString()
      const viewedId = (viewed.userId || viewed.id || viewed._id || '').toString()
      if (meId && viewedId && meId === viewedId) return true
      const meEmail = (root.currentUserEmail || '').toLowerCase()
      const cmp = (email || viewed.email || '').toLowerCase()
      if (!cmp) return false
      return meEmail === cmp
    }
  },

  flow: 'x',
  align: 'center',
  gap: 'Z',
  padding: 'Z',
  borderRadius: 'Z',

  Avatar: {
    align: 'center center',
    boxSize: 'B',
    minWidth: 'B',
    round: '50%',
    cursor: 'pointer',
    src: (el, s) => el.call('avatarUrlFor', s.email),
    onClick: (e, el, s) => {
      const slug = (s.email || '').split('@')[0]
      if (slug && typeof window !== 'undefined') {
        window.history.pushState({}, '', `/user/${slug}`)
        // `window.PopStateEvent` (not the bare global) — frank's
        // known-globals allowlist doesn't recognize `PopStateEvent` by name;
        // same object, just resolved off the already-guarded `window`.
        window.dispatchEvent(new window.PopStateEvent('popstate'))
      }
    }
  },

  Meta: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    Name: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => el.call('polyglot', s.name || '') || (s.email || '').split('@')[0]
    },

    Email: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      text: (el, s) => s.email || ''
    }
  },

  FollowBtn: {
    extends: 'Button',
    hide: (el, s) => {
      if (!s.email) return true
      return el.scope.isSelf(s.root, s.email)
    },
    padding: 'X A',
    borderRadius: 'Y',
    fontSize: 'Y',
    fontWeight: '700',
    cursor: 'pointer',
    // Was an outlined button — same fix + rationale as Hero's FollowBtn.
    background: (el, s) => (el.scope.isFollowing(s.root, s.email) ? 'overlay' : 'blue'),
    color: (el, s) => (el.scope.isFollowing(s.root, s.email) ? 'caption' : 'fillInk'),
    text: (el, s) =>
      el.call(
        'polyglot',
        el.scope.isFollowing(s.root, s.email)
          ? 'account.userProfile.following'
          : 'account.userProfile.follow'
      ),
    onClick: (e, el, s) => el.scope.toggleFollow(s.root, s.email)
  }
}
